import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { downloadBlob, exportAPI, projectTypesAPI, projectsAPI, usersAPI } from '../utils/api';
import { PageHeader, Badge, SearchInput, Modal, FormField, SelectField, EmptyState, ConfirmDialog } from '../components/layout/UI';
import { clientsAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { isAdminRole } from '../utils/rolesConfig';
import { apiError, useConfirm, useToast } from '../components/Toast';

import { useMaster } from '../hooks/useMasters';
import { usePagePerms } from '../PagePermsContext';
import { Download, Folder, Plus, RotateCcw, SquarePen, Trash2, X } from 'lucide-react';
import Pager, { usePaging } from '../components/Pager';
import { useSelection, SelectAll, RowCheck, BulkBar } from '../components/BulkActions';

// ── Icons ─────────────────────────────────────────────────────────────────
const Pen = () => <SquarePen size={13} />;
const Tr = () => <Trash2 size={13} />;
const Pl = () => <Plus size={14} />;
const Dl = () => <Download size={14} />;

// ── Project type definitions ───────────────────────────────────────────────
export const PROJECT_TYPES = {
  'General': {
    icon: 'GEN',
    color: '#2E539F',
    description: 'General-purpose projects for internal work and miscellaneous tasks',
    badge: 'border-brand-500 text-brand-600 dark:text-brand-400',
    extraFields: [],
  },
  'Sales': {
    icon: 'SAL',
    color: '#2E539F',
    description: 'Pre-sales activities, proposals, and deal pipeline management',
    badge: 'border-emerald-500 text-emerald-600 dark:text-emerald-400',
    extraFields: ['estimated_value', 'contract_number'],
  },
  'Customer Onboarding': {
    icon: 'ONB',
    color: '#2E539F',
    description: 'Structured onboarding process from kickoff through go-live and hypercare',
    badge: 'border-amber-500 text-amber-600 dark:text-amber-400',
    extraFields: ['client_contact', 'client_email', 'client_phone', 'go_live_date', 'onboarding_stage'],
  },
  'Implementation': {
    icon: 'IMP',
    color: '#2E539F',
    description: 'Deploying the Q-Lana platform at a client (financial institution or fund manager) through Preparation, Assessment, Configuration & Customization, and Deployment',
    badge: 'border-purple-500 text-purple-600 dark:text-purple-400',
    extraFields: ['go_live_date', 'contract_number', 'client_contact', 'digital_maturity_stage'],
  },
};

const ONBOARDING_STAGES = ['Kickoff','Requirements','Configuration','Testing','Training','Go Live','Hypercare'].map(v=>({value:v,label:v}));
const MATURITY_STAGES = [
  'Business as Usual','Present and Active','Formalized','Strategic','Converged','Innovative'
].map((v,i)=>({value:v,label:`#${i+1} - ${v}`}));
const S_OPTS_FALLBACK     = ['Not Started','Active','On Hold','Completed'].map(v=>({value:v,label:v}));
const P_OPTS_FALLBACK     = ['Low','Medium','High','Critical'].map(v=>({value:v,label:v}));
/**
 * Presentation for the types this page knows by name — colour, badge and the
 * extra fields each one shows. It is NOT the list of types that exist: that
 * lives in the project_types table and is managed in Admin -> Project Types.
 *
 * A type added in the admin panel never appeared here, because this object was
 * the dropdown. It now supplies the styling only, and anything it does not
 * recognise falls back to General's presentation rather than disappearing.
 */
const typeInfoFor = (name) => PROJECT_TYPES[name] || {
  ...PROJECT_TYPES['General'],
  description: '',
};

const defaultForm = {
  name:'', client:'', client_id:'', description:'', status:'Not Started', priority:'Medium',
  project_type:'General', start_date:'', end_date:'', assigned_users:[],
  estimated_value:'', client_contact:'', client_email:'', client_phone:'',
  go_live_date:'', contract_number:'', onboarding_stage:'', digital_maturity_stage:'',
};

export default function ProjectsPage() {
  // Admin → Page Access decides whether this role may create here.
  const { canWrite } = usePagePerms();
  // Dropdowns come from Masters so an Admin can relabel and reorder them.
  // The _FALLBACK arrays above are used when Masters has nothing configured.
  const S_OPTS = useMaster('project_status', S_OPTS_FALLBACK);
  const P_OPTS = useMaster('priority', P_OPTS_FALLBACK);
  const { user: me } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  /** Matches the API: the creator, or Admin level. Nobody else. */
  const canDelete = (p) => p?.created_by === me?.id || isAdminRole(me?.role);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  // All / Mine / Deleted as one control — the three are mutually exclusive.
  const [scope, setScope] = useState('all');
  // One pager for every list, so they all count and phrase it the same way.
  const paging = usePaging(scope);

  // ?no_roles=1 comes from the admin dashboard's "Projects with no roles" card.
  const [sp, setSp] = useSearchParams();
  const noRoles = sp.get('no_roles') === '1';
  const clearFocus = () => { const n = new URLSearchParams(sp); n.delete('no_roles'); setSp(n); };
  const [typeFilter, setTypeFilter] = useState('');
  // The types that actually exist, from the admin-managed table. The
  // hardcoded names are the fallback for a fresh database.
  const [types, setTypes] = useState(() => Object.keys(PROJECT_TYPES));

  useEffect(() => {
    let cancelled = false;
    projectTypesAPI.getAll()
      .then(r => {
        if (cancelled) return;
        const names = (r.data || [])
          .filter(t => t.is_active !== false)
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map(t => t.name);
        if (names.length) setTypes(names);
      })
      .catch(() => { /* keep the fallback; the form must stay usable */ });
    return () => { cancelled = true; };
  }, []);

  const TYPE_OPTS = types.map(v => ({ value: v, label: v }));
  const [statusFilter, setStatusFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [form,     setForm]     = useState({ ...defaultForm });
  const [clients,  setClients]  = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [deleteId, setDeleteId] = useState(null);





  const discard = async (row) => {
    const ok = await confirm(
      'Remove this from your Deleted tab? You will not be able to see it after '
      + 'this, though an administrator can still restore it.',
      { title: 'Remove from Deleted', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await projectsAPI.discard(row.id);
      toast.success('Removed from your Deleted tab');
      load();
    } catch (e) { toast.error(apiError(e, 'Could not remove it.')); }
  };
  const restore = async (row) => {
    try {
      await projectsAPI.restore(row.id);
      toast.success('Restored');
      load();
    } catch (e) { toast.error(apiError(e, 'Could not restore that project.')); }
  };
  const load = useCallback(async () => {
    try {
      const [pR, uR] = await Promise.all([projectsAPI.getAll({ mine: scope === 'mine' ? 'true' : undefined,
                             scope: scope === 'deleted' ? 'deleted' : undefined,
                             no_roles: noRoles ? '1' : undefined, limit: paging.limit, offset: paging.offset }), usersAPI.getAll()]);
      setProjects(pR.data); setUsers(uR.data);
      paging.readHeaders(pR);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [scope, noRoles, paging.offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { clientsAPI.getAll({ limit: 0 }).then(r => setClients(r.data||[])).catch(e => toast.error(apiError(e, 'Could not load this page.'))); }, []);

  const openCreate = () => { setEditProject(null); setForm({ ...defaultForm }); setModalOpen(true); };
  const openEdit   = (p) => {
    setEditProject(p);
    setForm({
      name: p.name, client: p.client||'', description: p.description||'',
      status: p.status, priority: p.priority,
      project_type: p.project_type||'General',
      start_date: p.start_date?.split('T')[0]||'',
      end_date:   p.end_date?.split('T')[0]||'',
      assigned_users: (p.assigned_users||[]).map(u => typeof u==='object' ? u.id : u),
      client_id: p.client_id||'',
      estimated_value: p.estimated_value||'',
      client_contact:  p.client_contact||'',
      client_email:    p.client_email||'',
      client_phone:    p.client_phone||'',
      go_live_date:    p.go_live_date?.split('T')[0]||'',
      contract_number: p.contract_number||'',
      onboarding_stage:p.onboarding_stage||'',
      digital_maturity_stage:p.digital_maturity_stage||'',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      editProject ? await projectsAPI.update(editProject.id, form) : await projectsAPI.create(form);
      toast.success(editProject ? 'Project updated' : 'Project created');
      setModalOpen(false); load();
    } catch(e) { toast.error(apiError(e, 'Failed to save project.')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await projectsAPI.delete(deleteId);
      toast.success('Project deleted');
      toast.success('Project deleted');
    } catch(e) {
      // A 409 here means it still has children — the message names them.
      toast.error(apiError(e, 'Could not delete that project.'));
    } finally { setDeleteId(null); load(); }
  };

  const typeInfo = typeInfoFor(form.project_type);
  const showField = (f) => typeInfo.extraFields.includes(f);

  const filtered = projects.filter(p =>
    (!typeFilter   || p.project_type === typeFilter) &&
    (!statusFilter || p.status === statusFilter) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || p.client?.toLowerCase().includes(search.toLowerCase()))
  );

  // Ticked rows. Scoped to what is on screen, so a filter cannot hide
  // something that is still selected.
  // Declared AFTER `filtered`, deliberately: a const cannot be read
  // above its own declaration, and doing so crashes the page at render
  // with "Cannot access 'filtered' before initialization".
  const sel = useSelection(filtered);

  // Counts per type for the filter tabs
  const typeCounts = types.reduce((acc, t) => {
    acc[t] = projects.filter(p => p.project_type === t).length;
    return acc;
  }, {});

  return (
    <div>
      {error && (
        <div className="pt-2">
          <div className="p-3 rounded-xl text-sm mb-2" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
            ⚠ {error} <button className="ml-2 underline text-xs" onClick={()=>setError('')}>Dismiss</button>
          </div>
        </div>
      )}
      <PageHeader title="Projects" subtitle="Manage all projects by type and status"
        actions={
          <div className="flex gap-2">
            <button onClick={async()=>{ const r=await exportAPI.exportProjects(); downloadBlob(r.data,'projects.xlsx'); }} className="btn-secondary flex items-center gap-1.5 text-sm"><Dl /> Export</button>
            {canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><Pl /> New Project</button>)}
          </div>
        }
      />

      {/* Type filter tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setTypeFilter('')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${!typeFilter ? 'bg-brand-500 text-white border-brand-500' : 'border-[var(--border)]'}`}
          style={!typeFilter ? {} : { color:'var(--text-secondary)', background:'var(--surface)' }}>
          All <span className="text-xs px-1.5 py-0.5 rounded-full bg-white/20">{projects.length}</span>
        </button>
        {Object.entries(PROJECT_TYPES).map(([type, info]) => (
          <button key={type} onClick={() => setTypeFilter(type)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${typeFilter===type ? 'text-white border-transparent' : 'border-[var(--border)]'}`}
            style={typeFilter===type ? { background: info.color } : { color:'var(--text-secondary)', background:'var(--surface)' }}>
            {type}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${typeFilter===type ? 'bg-white/25' : 'bg-[var(--surface-alt)]'}`}>
              {typeCounts[type]}
            </span>
          </button>
        ))}
      </div>

      {/* Search + status filter */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex-1 min-w-48 max-w-xs">
          <SearchInput value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search projects..." />

        </div>
        {noRoles && (
          <button onClick={clearFocus}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm self-start"
            style={{ background:'rgba(255,200,73,.20)', color:'#8A6400' }}>
            No roles assigned <X size={13} />
          </button>
        )}
        {/* All / Mine / Deleted. Deleted shows only what you could have seen
            while it was live — deletion must never reveal something hidden. */}
        <div className="flex rounded-lg border overflow-hidden text-sm self-start"
          style={{ borderColor:'var(--border)' }}>
          {[['all','All'],['mine','Mine'],['deleted','Deleted']].map(([v,l]) => (
            <button key={v} onClick={() => setScope(v)}
              className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${scope===v ? 'bg-brand-500 text-white' : ''}`}
              style={scope!==v ? { color:'var(--text-secondary)', background:'var(--surface)' } : {}}>
              {v === 'deleted' && <Trash2 size={13} />}
              {l}
              {scope===v && <span className="font-bold">{filtered.length}</span>}
            </button>
          ))}
        </div>
        <select className="select w-40 text-sm" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {S_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
      ) : filtered.length === 0 ? (
        <div className="card"><EmptyState icon={Folder} title="No projects found" description="Create your first project"
          action={canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><Pl /> New Project</button>)}/></div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                <th className="px-4 py-3 w-10">
                  <SelectAll allChecked={sel.allChecked} someChecked={sel.someChecked}
                    onToggle={sel.toggleAll} />
                </th>
                {['Name / Client','Type','Status','Priority','Progress','Team',''].map(h=>(
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const pt = typeInfoFor(p.project_type);
                const pct = parseInt(p.progress) || 0;
                return (
                  <tr key={p.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors cursor-pointer"
                    style={{ borderColor:'var(--border)' }} onClick={() => navigate(`/projects/${p.id}`)}>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <RowCheck checked={sel.isSelected(p.id)}
                        onToggle={() => sel.toggle(p.id)} label={p.name} />
                    </td>

                    {/* Name + client — primary identifier, always first */}
                    <td className="px-4 py-3">
                      <p className="font-semibold" style={{ color:'var(--text)' }}>{p.name}</p>
                      <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{p.client}</p>
                    </td>

                    {/* Type badge */}
                    <td className="px-4 py-3">
                      <span className={`badge border text-xs ${pt.badge}`}>
                        {p.project_type}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3" onClick={e=>e.stopPropagation()}>
                      <select value={p.status}
                        onChange={async e=>{
                          try {
                            await projectsAPI.update(p.id,{ status:e.target.value });
                            toast.success(`Status set to ${e.target.value}`);
                          } catch(err) { toast.error(apiError(err, 'Could not change that status.')); }
                          finally { load(); }
                        }}
                        className="text-xs rounded-lg px-2 py-1 border font-medium cursor-pointer outline-none"
                        style={{ background:'var(--surface)', borderColor:'var(--border)', color:'var(--text)' }}>
                        {S_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>

                    <td className="px-4 py-3"><Badge value={p.priority} type="priority"/></td>

                    {/* Progress */}
                    <td className="px-4 py-3" style={{ minWidth:100 }}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                          <div className="h-full rounded-full bg-brand-500" style={{ width:`${pct}%` }}/>
                        </div>
                        <span className="text-xs font-medium" style={{ color:'var(--text-secondary)' }}>{pct}%</span>
                      </div>
                    </td>

                    {/* Team */}
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex -space-x-1.5">
                        {(p.team_members||[]).slice(0,4).map(m=>(
                          <div key={m.id} className="w-6 h-6 rounded-lg border-2 flex items-center justify-center font-bold text-white text-xs"
                            style={{ background:m.avatar_color||'#2E539F', borderColor:'var(--surface)' }} title={m.name}>
                            {m.name?.[0]}
                          </div>
                        ))}
                        {(p.team_members||[]).length>4&&<div className="w-6 h-6 rounded-lg border-2 flex items-center justify-center text-xs font-bold text-white bg-gray-400" style={{ borderColor:'var(--surface)' }}>+{p.team_members.length-4}</div>}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3" onClick={e=>e.stopPropagation()}>
                      <div className="flex gap-1">
                        {scope === 'deleted' ? (
                          <>
                          {/* In the bin the only sensible action is to put it
                             back. Editing or deleting it again are nonsense. */}
                          <button onClick={e=>{e.stopPropagation();restore(p);}}
                            className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 transition-colors hover:bg-[var(--surface-alt)]"
                            style={{ borderColor:'var(--border)', color:'#2E539F' }}>
                            <RotateCcw size={12} /> Restore
                          </button>
                          {/* The second delete. It takes this out of YOUR
                              Deleted tab and nothing more — an administrator
                              can still get it back. */}
                          <button onClick={e=>{e.stopPropagation();discard(p);}}
                            className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                            style={{ borderColor:'var(--border)', color:'#ef4444' }}>
                            <Trash2 size={12} /> Delete
                          </button>
                          </>
                        ) : (
                          <>
                          <button onClick={()=>openEdit(p)} className="p-1.5 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}><Pen /></button>

                          {canDelete(p) && (

                            <button onClick={e=>{e.stopPropagation();setDeleteId(p.id);}} title="Delete"

                              className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><Tr /></button>

                          )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)} title={editProject ? 'Edit Project' : 'New Project'} size="lg">
        <div className="space-y-4">

          {/* Project Type selector — prominent at top */}
          <div>
            <label className="label">Project Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {types.map(type => { const info = typeInfoFor(type); return (
                <button key={type} type="button" onClick={()=>setForm(f=>({...f,project_type:type}))}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${form.project_type===type?'border-brand-500':'border-[var(--border)] hover:border-brand-500/40'}`}
                  style={{ background:'var(--surface-alt)' }}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: form.project_type===type ? '#2E539F' : 'var(--text)' }}>{type}</p>
                    <p className="text-xs leading-tight mt-0.5" style={{ color:'var(--text-secondary)' }}>{info.description}</p>
                  </div>
                </button>
              ); })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Project Name" required><input className="input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Project name"/></FormField>
            <FormField label="Client Name"><input className="input" value={form.client} onChange={e=>setForm(f=>({...f,client:e.target.value}))} placeholder="Client name (free text)"/></FormField>
            <FormField label="Link to Client Record">
              <select className="select" value={form.client_id||''} onChange={e=>setForm(f=>({...f,client_id:e.target.value}))}>
                <option value="">None — standalone project</option>
                {clients.map(cl=>(
                  <option key={cl.id} value={cl.id}>
                    {cl.company_name || `${cl.first_name||''} ${cl.last_name||''}`.trim()}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <FormField label="Description"><textarea className="input" rows={2} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="What is this project about?"/></FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Status"><SelectField value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} options={S_OPTS} placeholder=""/></FormField>
            <FormField label="Priority"><SelectField value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))} options={P_OPTS} placeholder=""/></FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Start Date"><input type="date" className="input" value={form.start_date} onChange={e=>setForm(f=>({...f,start_date:e.target.value}))}/></FormField>
            <FormField label="End Date"><input type="date" className="input" value={form.end_date} onChange={e=>setForm(f=>({...f,end_date:e.target.value}))}/></FormField>
          </div>

          {/* ── Type-specific fields ───────────────────────────────────── */}
          {(showField('estimated_value') || showField('contract_number')) && (
            <div className="rounded-xl border p-4 space-y-3" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{form.project_type} Details</p>
              <div className="grid grid-cols-2 gap-4">
                {showField('estimated_value') && (
                  <FormField label="Estimated Value (USD)"><input type="number" className="input" value={form.estimated_value} onChange={e=>setForm(f=>({...f,estimated_value:e.target.value}))} placeholder="0.00"/></FormField>
                )}
                {showField('contract_number') && (
                  <FormField label="Contract / Reference #"><input className="input" value={form.contract_number} onChange={e=>setForm(f=>({...f,contract_number:e.target.value}))} placeholder="CONTRACT-001"/></FormField>
                )}
              </div>
            </div>
          )}

          {(showField('client_contact') || showField('client_email') || showField('client_phone')) && (
            <div className="rounded-xl border p-4 space-y-3" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: typeInfo.color }}>Client Contact</p>
              <div className="grid grid-cols-1 gap-3">
                {showField('client_contact') && (
                  <FormField label="Contact Person"><input className="input" value={form.client_contact} onChange={e=>setForm(f=>({...f,client_contact:e.target.value}))} placeholder="Jane Doe"/></FormField>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {showField('client_email') && (
                    <FormField label="Email"><input type="email" className="input" value={form.client_email} onChange={e=>setForm(f=>({...f,client_email:e.target.value}))} placeholder="client@email.com"/></FormField>
                  )}
                  {showField('client_phone') && (
                    <FormField label="Phone"><input type="tel" className="input" value={form.client_phone} onChange={e=>setForm(f=>({...f,client_phone:e.target.value}))} placeholder="+250..."/></FormField>
                  )}
                </div>
              </div>
            </div>
          )}

          {(showField('go_live_date') || showField('onboarding_stage') || showField('digital_maturity_stage')) && (
            <div className="rounded-xl border p-4 space-y-3" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: typeInfo.color }}>Timeline & Stage</p>
              <div className="grid grid-cols-2 gap-4">
                {showField('go_live_date') && (
                  <FormField label="Go-Live Date"><input type="date" className="input" value={form.go_live_date} onChange={e=>setForm(f=>({...f,go_live_date:e.target.value}))}/></FormField>
                )}
                {showField('onboarding_stage') && (
                  <FormField label="Onboarding Stage"><SelectField value={form.onboarding_stage} onChange={e=>setForm(f=>({...f,onboarding_stage:e.target.value}))} options={ONBOARDING_STAGES} placeholder="Select stage"/></FormField>
                )}
                {showField('digital_maturity_stage') && (
                  <FormField label="Digital Maturity Stage">
                    <SelectField value={form.digital_maturity_stage} onChange={e=>setForm(f=>({...f,digital_maturity_stage:e.target.value}))} options={MATURITY_STAGES} placeholder="Assess client's stage"/>
                  </FormField>
                )}
              </div>
              {showField('digital_maturity_stage') && (
                <p className="text-xs leading-relaxed" style={{ color:'var(--text-secondary)' }}>
                  Per Q-Lana's implementation methodology, assess the client's digital transformation maturity before kickoff (1=Business as Usual through 6=Innovative).
                </p>
              )}
            </div>
          )}

          {/* Team members */}
          <FormField label="Team Members">
            <div className="max-h-36 overflow-y-auto border rounded-xl p-2 space-y-0.5" style={{ borderColor:'var(--border)' }}>
              {users.filter(u=>u.is_active).map(u=>(
                <label key={u.id} className="flex items-center gap-2.5 cursor-pointer p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors">
                  <input type="checkbox" checked={(form.assigned_users||[]).includes(u.id)}
                    onChange={e=>setForm(f=>({...f,assigned_users:e.target.checked?[...(f.assigned_users||[]),u.id]:(f.assigned_users||[]).filter(id=>id!==u.id)}))}/>
                  <div className="w-6 h-6 rounded flex items-center justify-center font-bold text-white text-xs" style={{ background:u.avatar_color||'#2E539F' }}>{u.name[0]}</div>
                  <span className="text-sm" style={{ color:'var(--text)' }}>{u.name}</span>
                  <span className="text-xs ml-auto" style={{ color:'var(--text-secondary)' }}>{u.role}</span>
                </label>
              ))}
            </div>
          </FormField>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving||!form.name||!form.client} className="btn-primary flex items-center gap-2"
              style={{}}>
              {saving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              {editProject ? 'Update' : 'Create'} Project
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={handleDelete} title="Delete Project" message="Delete this project? It must already be empty — a project that still has tasks, bugs or change requests cannot be deleted until those are removed or reassigned."/>

    <Pager {...paging} onChange={paging.setOffset} noun="projects" />
    <BulkBar bulk={projectsAPI.bulk} view={scope === 'deleted' ? 'deleted' : 'live'}
      selected={sel.selected} label="project" labelPlural="projects"
      onClear={sel.clear} onDone={load} />
    </div>
  );
}
